// deriveBerufFromConnections.ts — leitet aus den Firmen-Verbindungen einer Person
// die zu übernehmenden Beruf-Felder ab (für Autofill in der Anlage und den späteren
// PROD-Backfill). Rein, ohne Seiteneffekte → unit-testbar.
//
// Regeln (aus der Fachvorgabe):
//   - Verantwortlichkeit → Tätigkeit (jobOccupation)
//   - Firma → Arbeitgeber (jobEmployer)
//   - Gesellschafter → Status „Selbstständig", sonst „Angestellt"
//   - Branche der Firma → Branche der Person (jobIndustry)
// Bei mehreren Firmen füllt die „primäre" Verbindung (interne Anstellung
// bevorzugt, sonst die erste).

import { LABEL_GF, LABEL_GESELLSCHAFTER, LABEL_GF_GESELLSCHAFTER } from "./gfGesellschafter";

/** Rollen-Form (kompatibel zu VerantRole des Verantwortlichkeit-Inputs). */
export type BerufRole =
  | { kind: "gf" }
  | { kind: "gesellschafter"; istGeschaeftsfuehrer?: boolean }
  | { kind: "mensch"; verantwortlichkeit: string };

export interface BerufConnection {
  /** true = extern (Mensch fürs Unternehmen), false = interne Anstellung. */
  isExtern: boolean;
  roles: BerufRole[];
  /** Name der (Arbeitgeber-)Firma. */
  companyName?: string;
  /** Branche der Firma (bereits als Text aufgelöst). */
  companyBranche?: string;
}

export interface DerivedBeruf {
  jobStatus?: string;
  jobEmployer?: string;
  jobOccupation?: string;
  jobIndustry?: string;
}

/** Sprechendes Label einer einzelnen Rolle. */
export function roleLabel(role: BerufRole): string {
  if (role.kind === "gf") return LABEL_GF;
  if (role.kind === "gesellschafter")
    return role.istGeschaeftsfuehrer ? LABEL_GF_GESELLSCHAFTER : LABEL_GESELLSCHAFTER;
  return (role.verantwortlichkeit || "").trim();
}

/** true, wenn die Verbindung eine Gesellschafter-Rolle enthält. */
export function isGesellschafter(conn: BerufConnection): boolean {
  return conn.roles.some((r) => r.kind === "gesellschafter");
}

/** Primäre Verbindung: interne Anstellung bevorzugt, sonst die erste. */
export function selectPrimary(
  conns: BerufConnection[],
): BerufConnection | undefined {
  return conns.find((c) => !c.isExtern) ?? conns[0];
}

/** Leitet die Beruf-Felder aus den Verbindungen ab (primäre Verbindung). */
export function deriveBerufFromConnections(
  conns: BerufConnection[],
): DerivedBeruf {
  const primary = selectPrimary(conns);
  if (!primary) return {};
  const occupation = primary.roles
    .map(roleLabel)
    .filter(Boolean)
    .join(", ");
  const out: DerivedBeruf = {
    // Schreibweise aus dem Katalog `ContactBerufsstatus` — das Autofill muss
    // dieselben Werte liefern wie das Dropdown, sonst erscheint der
    // vorbelegte Status dort als leer.
    jobStatus: isGesellschafter(primary) ? "Selbstständige/r" : "Angestellte/r",
  };
  if (primary.companyName?.trim()) out.jobEmployer = primary.companyName.trim();
  if (occupation) out.jobOccupation = occupation;
  if (primary.companyBranche?.trim())
    out.jobIndustry = primary.companyBranche.trim();
  return out;
}
